import * as React from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { StatusPill } from '@/components/ui/status-pill'
import { cn } from '@/lib/utils'

export type ActivityTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/** One labelled value: a party role and its people, or a row of technical detail. */
export type ActivityFact = { label: string; value: React.ReactNode }

export type ActivityEntry = {
  id: string
  /** Decorative; the title names the kind. */
  icon: React.ReactNode
  title: string
  when?: string
  detail?: string
  status?: { tone: ActivityTone; label: string }
  /** A plain-language outcome, failure or reason, for everyone who can see the entry. */
  description?: string
  /** Who was involved, e.g. the party of a visit. */
  facts?: ActivityFact[]
  /** A muted line for something incomplete or unavailable on this entry. */
  notes?: string[]
  /** Technical detail, shown behind a disclosure; leave it out when the caller may not see it. */
  diagnostic?: ActivityFact[]
  /** Entries that belong to this one, such as the messages and orders of a visit, in their own order. */
  items?: ActivityEntry[]
}

export type ActivityGroup = { id: string; heading: string; entries: ActivityEntry[] }

export type ActivityHistoryLabels = {
  showDetails: string
  hideDetails: string
  /** Names the list of an entry's own items, e.g. "On this visit". */
  items: string
}

/*
 * ActivityHistory: one chronological list in groups, an `h3` and an ordered
 * list per group (e.g. Later today, Earlier today, then one per month). Each
 * entry has a decorative icon, a title naming its kind, its time and detail,
 * and a StatusPill with text. An entry can carry a plain-language outcome,
 * labelled facts (whose values may hold links the caller may follow), muted
 * notes, its own items as a nested list, and technical detail behind a
 * disclosure (`aria-expanded`/`aria-controls`). The feature decides what each
 * caller may see; this component shows what it is given. Long text wraps.
 */
export function ActivityHistory({
  groups,
  labels,
}: {
  groups: ActivityGroup[]
  labels: ActivityHistoryLabels
}) {
  const baseId = React.useId()
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group, index) => {
        const headingId = `${baseId}-group-${index}`
        return (
          <section key={group.id} aria-labelledby={headingId} className="flex flex-col gap-1">
            <h3 id={headingId} className="text-sm font-semibold text-fg-muted">
              {group.heading}
            </h3>
            <ol className="flex flex-col">
              {group.entries.map((entry) => (
                <Entry key={entry.id} entry={entry} labels={labels} />
              ))}
            </ol>
          </section>
        )
      })}
    </div>
  )
}

function Entry({ entry, labels, nested = false }: { entry: ActivityEntry; labels: ActivityHistoryLabels; nested?: boolean }) {
  const [open, setOpen] = React.useState(false)
  const detailId = React.useId()
  const diagnostic = entry.diagnostic?.length ? entry.diagnostic : null

  return (
    <li
      className={cn(
        'flex gap-3',
        nested ? 'py-2' : 'border-b border-line py-3 last:border-b-0',
      )}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-sm bg-surface-subtle text-fg-muted [&_svg]:size-4"
      >
        {entry.icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-medium text-fg-strong wrap-anywhere">{entry.title}</p>
          {entry.status ? <StatusPill tone={entry.status.tone}>{entry.status.label}</StatusPill> : null}
        </div>
        {entry.when || entry.detail ? (
          <p className="flex flex-wrap gap-x-2 text-xs text-fg-muted tabular-nums wrap-anywhere">
            {entry.when ? <span>{entry.when}</span> : null}
            {entry.detail ? <span>{entry.detail}</span> : null}
          </p>
        ) : null}
        {entry.description ? <p className="max-w-prose text-sm text-fg wrap-anywhere">{entry.description}</p> : null}
        {entry.facts?.length ? <Facts facts={entry.facts} className="mt-1 text-sm" /> : null}
        {entry.notes?.map((note) => (
          <p key={note} className="text-sm text-fg-muted">
            {note}
          </p>
        ))}
        {diagnostic ? (
          <div className="mt-1">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={detailId}
              onClick={() => setOpen((value) => !value)}
              className="inline-flex min-h-6 items-center gap-1 rounded-sm text-xs font-medium text-fg-muted hover:text-fg"
            >
              {open ? <ChevronDown aria-hidden="true" className="size-4" /> : <ChevronRight aria-hidden="true" className="size-4" />}
              {open ? labels.hideDetails : labels.showDetails}
            </button>
          </div>
        ) : null}
        {diagnostic ? (
          <div id={detailId} hidden={!open}>
            <Facts
              facts={diagnostic}
              mono
              className="mt-1 rounded-sm border border-line bg-surface-subtle px-3 py-2 text-xs"
            />
          </div>
        ) : null}
        {entry.items?.length ? (
          <ol aria-label={labels.items} className="mt-1 flex flex-col border-l border-line pl-3">
            {entry.items.map((item) => (
              <Entry key={item.id} entry={item} labels={labels} nested />
            ))}
          </ol>
        ) : null}
      </div>
    </li>
  )
}

/* Label over value below `sm`, side by side from `sm` up. */
function Facts({ facts, mono = false, className }: { facts: ActivityFact[]; mono?: boolean; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-y-1', className)}>
      {facts.map((fact) => (
        <React.Fragment key={fact.label}>
          <dt className="text-fg-muted">{fact.label}</dt>
          <dd className={cn('mb-1 text-fg wrap-anywhere sm:mb-0', mono && 'font-mono')}>{fact.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}
